import React, { useRef, useState } from "react";
import { ArrowUpTrayIcon, DocumentTextIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import { Loader2, AlertCircle } from "lucide-react";
import { formatProviderSubtitle, formatProviderTitle } from "@/lib/cvImportProvider";
import { getWarningUserMessage, getAiErrorUserMessage, getAiErrorCause } from "@/lib/cvWarningMessages";

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

export const CANONICAL_IMPORT_STEPS = [
    {
        id: "upload",
        label: "Document reçu",
        subtitle: "Téléversement et empreinte SHA-256",
    },
    {
        id: "docling_extraction",
        label: "Extraction documentaire",
        subtitle: "Lecture Docling / OCR et SourceLedger",
    },
    {
        id: "semantic_analysis",
        label: "Analyse IA",
        subtitle: "Analyse IA en attente",
    },
    {
        id: "cv_structuring",
        label: "Structuration",
        subtitle: "FactLedger et projection canonique",
    },
    {
        id: "fidelity_check",
        label: "Contrôle documentaire",
        subtitle: "EvidenceValidator et fidélité source",
    },
    {
        id: "draft_preparation",
        label: "Génération du brouillon",
        subtitle: "Normalisation et rapport qualité",
    },
    {
        id: "finalization",
        label: "Finalisation",
        subtitle: "Sauvegarde et disponibilité du CV",
    },
];

export const formatElapsedHuman = (sec) => {
    const total = Math.max(0, Math.floor(sec || 0));
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${String(m).padStart(2, "0")} min ${String(s).padStart(2, "0")} s`;
};

export const formatElapsedDigital = (sec) => {
    const total = Math.max(0, Math.floor(sec || 0));
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

export const formatStepDuration = (sec) => {
    if (sec == null || isNaN(sec)) return "";
    const num = Number(sec);
    if (num >= 60) {
        const m = Math.floor(num / 60);
        const s = Math.floor(num % 60);
        return `${String(m).padStart(2, "0")} min ${String(s).padStart(2, "0")} s`;
    }
    return `${num.toFixed(1)} s`;
};

const CvImportStep = ({
    compact = false,
    onFileSelected,
    isLoading,
    importProgress = null,
    importError = null,
    importWarnings = [],
    lastImportResult = null,
    onOpenBuilder = null,
    onRetry = null,
}) => {
    const fileInputRef = useRef(null);
    const [dragActive, setDragActive] = useState(false);
    const [loadingTime, setLoadingTime] = useState(0);
    const [localValidationError, setLocalValidationError] = useState("");

    React.useEffect(() => {
        let interval;
        if (isLoading) {
            setLoadingTime(0);
            interval = setInterval(() => {
                setLoadingTime((t) => t + 1);
            }, 1000);
        } else {
            setLoadingTime(0);
        }
        return () => clearInterval(interval);
    }, [isLoading]);

    React.useEffect(() => {
        if (isLoading) {
            setLocalValidationError("");
        }
    }, [isLoading]);

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            validateAndSelect(e.dataTransfer.files[0]);
        }
    };

    const handleChange = (e) => {
        e.preventDefault();
        if (e.target.files && e.target.files[0]) {
            validateAndSelect(e.target.files[0]);
        }
    };

    const validateAndSelect = (file) => {
        // We accept PDF, DOCX, PPTX, PNG, JPG
        const validExtensions = ['.pdf', '.docx', '.pptx', '.png', '.jpg', '.jpeg'];
        const isValid = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
        if (!isValid) {
            setLocalValidationError("Format non supporté. Utilisez un fichier PDF, DOCX, PPTX, PNG ou JPG.");
            return;
        }

        if (file.size > MAX_FILE_SIZE_BYTES) {
            setLocalValidationError("Le fichier est trop volumineux (max 5 Mo).");
            return;
        }

        if (!isLoading) {
            setLocalValidationError("");
            onFileSelected(file);
        }
    };

    const visibleError = importError?.error_message || getAiErrorUserMessage(importError?.error_code) || localValidationError;
    const hasWarnings = Array.isArray(importWarnings) && importWarnings.length > 0;
    const hasImportErrorObject = Boolean(importError && typeof importError === "object");
    const providerSource = hasImportErrorObject ? importError : lastImportResult;
    const providerUsed = String(providerSource?.provider_used || "").trim();
    const providerPrimary = String(providerSource?.provider_primary || "").trim();
    const hasProviderMetadata = Boolean(providerUsed || providerPrimary);
    const loadingProviderKey = providerPrimary && providerPrimary.toLowerCase() !== "unknown"
        ? providerPrimary
        : (providerUsed && providerUsed.toLowerCase() !== "unknown" ? providerUsed : "vertex_ai");
    const providerTitle = formatProviderTitle(
        isLoading
            ? loadingProviderKey
            : (providerUsed || providerPrimary),
    );
    const providerSubtitle = formatProviderSubtitle(
        providerSource?.model_used,
        providerSource?.provider_fallback_used === true,
        isLoading,
    );
    const shouldShowProviderBadge = !isLoading && hasProviderMetadata;

    const totalElapsedSeconds = Math.max(
        loadingTime,
        Math.round(importProgress?.total_duration_seconds ?? importProgress?.elapsed_seconds ?? importProgress?.elapsed_s ?? loadingTime),
    );

    const stepsToRender = React.useMemo(() => {
        const backendMap = new Map((importProgress?.steps || []).map((s) => [s.step_id || s.id, s]));
        const hasFailedBackendStep = (importProgress?.steps || []).some((s) => s.status === "FAILED");
        const hasImportError = Boolean(importError && !isLoading);

        let encounteredFailure = false;

        return CANONICAL_IMPORT_STEPS.map((canon, index) => {
            const b = backendMap.get(canon.id);
            if (b) {
                const duration = b.duration_seconds ?? b.duration_s ?? (typeof b.duration_ms === "number" ? b.duration_ms / 1000 : null);
                if (b.status === "FAILED") {
                    encounteredFailure = true;
                }
                const resolvedStatus = encounteredFailure && b.status !== "FAILED" && b.status !== "COMPLETED"
                    ? "SKIPPED"
                    : (b.status || "PENDING");
                return {
                    id: canon.id,
                    label: b.label || canon.label,
                    status: resolvedStatus,
                    duration: duration,
                    startedAt: b.started_at,
                    completedAt: b.completed_at,
                    message: b.message || canon.subtitle,
                    error: b.error,
                    errorCode: b.error_code || b.errorCode,
                };
            }
            if (isLoading) {
                if (index === 0) {
                    return {
                        id: canon.id,
                        label: canon.label,
                        status: "RUNNING",
                        duration: null,
                        startedAt: null,
                        message: "Envoi en cours...",
                        error: null,
                        errorCode: null,
                    };
                }
            }
            // If an import error occurred but no steps were recorded by backend, mark appropriate step as FAILED
            if (hasImportError && !hasFailedBackendStep) {
                const failedTargetId = importError?.failed_step_id || (String(importError?.error_code || "").toLowerCase().includes("mapping") ? "cv_structuring" : "docling_extraction");
                if (canon.id === failedTargetId || (!CANONICAL_IMPORT_STEPS.some(s => s.id === failedTargetId) && canon.id === "docling_extraction")) {
                    encounteredFailure = true;
                    return {
                        id: canon.id,
                        label: canon.label,
                        status: "FAILED",
                        duration: null,
                        startedAt: null,
                        message: visibleError || "Échec de l'analyse",
                        error: visibleError || "Échec de l'analyse",
                        errorCode: importError?.error_code,
                    };
                }
                const targetIndex = CANONICAL_IMPORT_STEPS.findIndex(s => s.id === failedTargetId);
                if (targetIndex > 0 && index < targetIndex) {
                    return {
                        id: canon.id,
                        label: canon.label,
                        status: "COMPLETED",
                        duration: null,
                        startedAt: null,
                        message: canon.subtitle,
                        error: null,
                        errorCode: null,
                    };
                }
                return {
                    id: canon.id,
                    label: canon.label,
                    status: "SKIPPED",
                    duration: null,
                    startedAt: null,
                    message: canon.subtitle,
                    error: null,
                    errorCode: null,
                };
            }
            return {
                id: canon.id,
                label: canon.label,
                status: encounteredFailure ? "SKIPPED" : "PENDING",
                duration: null,
                startedAt: null,
                message: canon.subtitle,
                error: null,
                errorCode: null,
            };
        });
    }, [importProgress, isLoading, importError, visibleError]);

    const getRunningDuration = (step) => {
        if (step.duration != null) {
            return step.duration;
        }
        if (step.status === "RUNNING") {
            if (step.startedAt) {
                const stepStarted = new Date(step.startedAt).getTime();
                if (!isNaN(stepStarted)) {
                    const diffSec = (Date.now() - stepStarted) / 1000;
                    if (diffSec >= 0 && diffSec < 3600) {
                        return diffSec;
                    }
                }
            }
            return loadingTime;
        }
        return null;
    };

    const failedStep = stepsToRender.find((s) => s.status === "FAILED") || CANONICAL_IMPORT_STEPS[1];
    const errorCode = importError?.error_code || failedStep?.errorCode;
    const causeText = getAiErrorCause(errorCode) || importError?.cause || (typeof visibleError === "string" && visibleError.includes("Cause :") ? visibleError.split("Cause :")[1]?.trim() : "");
    const mainErrorMessage = importError?.error_message
        || (typeof visibleError === "string" && visibleError.includes("Cause :")
            ? visibleError.split("Cause :")[0]?.replace(/\.\s*$/, "").trim() + "."
            : (getAiErrorUserMessage(errorCode) || visibleError || "Le document n'a pas pu être analysé."));
    const correlationJobId = importError?.job_id || importProgress?.job_id;

    return (
        <div className={`flex flex-col items-center justify-center max-w-2xl mx-auto ${compact ? "py-2" : "py-12"}`}>
            {!compact && <div className="text-center mb-10">
                <h2 className="text-2xl font-heading font-bold text-white mb-2">
                    Import de CV via IA
                </h2>
                <p className="text-slate-400">
                    Déposez le CV original pour extraire automatiquement les données vers le format Lenvia.
                </p>
            </div>}

            {isLoading ? (
                <div
                    className="flex flex-col items-center justify-center p-8 bg-white/5 border border-white/10 rounded-2xl w-full"
                    aria-busy="true"
                    aria-live="polite"
                >
                    <Loader2 aria-hidden="true" className="w-10 h-10 text-lenvia-blue animate-spin mb-4" />
                    
                    {/* Lot 2: Chronomètre réel global */}
                    <h3 className="text-xl font-bold text-white mb-1">
                        Analyse du CV en cours — {formatElapsedHuman(totalElapsedSeconds)}
                    </h3>
                    <p className="text-xs text-slate-300 font-mono mb-4">
                        Temps écoulé : <strong className="text-white">{formatElapsedDigital(totalElapsedSeconds)}</strong>
                    </p>

                    <p className="text-xs text-slate-400 text-center max-w-sm mb-4">
                        Analyse du CV en cours... {loadingTime > 30
                            ? "C'est un peu plus long que prévu, l'analyse IA finalise les détails..."
                            : "L'analyse intelligente du CV extrait les compétences, expériences et la formation."}
                    </p>

                    {/* Stepper de progression réelle avec les 7 étapes canoniques (Lot 2 & Lot 3) */}
                    <div className="w-full max-w-md my-2 bg-slate-900/90 border border-slate-700/80 rounded-xl p-4 shadow-xl space-y-2.5 text-left">
                        {stepsToRender.map((step) => {
                            let icon = <span className="flex items-center justify-center w-5 h-5 rounded-full border border-slate-700 text-slate-500 text-[11px]">○</span>;
                            let textClass = "text-slate-400";
                            let statusBadge = <span className="ml-1.5 text-[11px] font-normal text-slate-500"> — En attente</span>;

                            const stepDuration = getRunningDuration(step);

                            if (step.status === "COMPLETED") {
                                icon = <span className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 font-bold text-[11px]">✓</span>;
                                textClass = "text-slate-200";
                                statusBadge = (
                                    <span className="ml-1.5 text-[11px] font-normal text-slate-400">
                                        {step.duration != null && `— ${formatStepDuration(step.duration)} `}
                                        — <span className="text-emerald-400 font-medium">Terminé</span>
                                    </span>
                                );
                            } else if (step.status === "RUNNING") {
                                icon = <span className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-500/20 text-blue-400 font-bold text-[11px] animate-pulse">◉</span>;
                                textClass = "text-white font-semibold";
                                statusBadge = (
                                    <span className="ml-1.5 text-[11px] font-normal text-blue-300">
                                        {stepDuration != null && `— ${formatStepDuration(stepDuration)} `}
                                        — <span className="text-blue-400 font-semibold animate-pulse">En cours</span>
                                    </span>
                                );
                            } else if (step.status === "PARTIAL") {
                                icon = <span aria-label="Étape partielle" className="flex items-center justify-center w-5 h-5 rounded-full bg-amber-500/20 text-amber-300 font-bold text-[11px]">!</span>;
                                textClass = "text-amber-200 font-semibold";
                                statusBadge = (
                                    <span className="ml-1.5 text-[11px] font-normal text-amber-300">
                                        {stepDuration != null && `— ${formatStepDuration(stepDuration)} `}
                                        — <span className="text-amber-400 font-semibold">Partiel</span>
                                    </span>
                                );
                            } else if (step.status === "FAILED") {
                                icon = <span className="flex items-center justify-center w-5 h-5 rounded-full bg-rose-500/20 text-rose-400 font-bold text-[11px]">✕</span>;
                                textClass = "text-rose-300 font-semibold";
                                statusBadge = (
                                    <span className="ml-1.5 text-[11px] font-normal text-rose-300">
                                        {stepDuration != null && `— ${formatStepDuration(stepDuration)} `}
                                        — <span className="text-rose-400 font-bold">En erreur</span>
                                    </span>
                                );
                            } else if (step.status === "SKIPPED") {
                                icon = <span className="flex items-center justify-center w-5 h-5 rounded-full bg-slate-800 text-slate-600 text-[11px]">⊘</span>;
                                textClass = "text-slate-500";
                                statusBadge = <span className="ml-1.5 text-[11px] font-normal text-slate-500"> — Non exécuté</span>;
                            }

                            return (
                                <div
                                    key={step.id}
                                    className={`flex items-start justify-between gap-3 p-2 rounded-lg transition-colors ${
                                        step.status === "RUNNING"
                                            ? "bg-blue-500/10 border border-blue-500/30"
                                            : step.status === "PARTIAL"
                                            ? "bg-amber-500/10 border border-amber-500/30"
                                            : step.status === "FAILED"
                                            ? "bg-rose-500/10 border border-rose-500/30"
                                            : ""
                                    }`}
                                >
                                    <div className="flex items-start gap-2.5">
                                        <div className="mt-0.5 shrink-0">{icon}</div>
                                        <div>
                                            <div className={`text-xs ${textClass}`}>
                                                <span>{step.label}</span>
                                                {statusBadge}
                                            </div>
                                            {step.message && step.status !== "FAILED" && (
                                                <div className="text-[10px] text-slate-400 whitespace-pre-line">{step.message}</div>
                                            )}
                                            {step.status === "FAILED" && (
                                                <div className="text-[10px] text-rose-300 mt-0.5 font-mono">
                                                    {step.error || step.message || "Erreur lors de l'exécution"}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        <div className="pt-2 border-t border-slate-800 flex items-center justify-between text-[11px] text-slate-400 font-mono">
                            <span>Temps total écoulé : <strong className="text-slate-200">{totalElapsedSeconds} s ({formatElapsedDigital(totalElapsedSeconds)})</strong></span>
                            <span className="flex items-center gap-1 text-blue-400 font-sans">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                En cours
                            </span>
                        </div>
                    </div>

                </div>
            ) : (
                <div
                    className={`flex flex-col items-center justify-center ${compact ? "p-6" : "p-12"} border-2 border-dashed rounded-2xl w-full cursor-pointer transition-colors ${
                        dragActive ? "border-lenvia-blue bg-lenvia-blue/5" : "border-white/20 bg-white/5 hover:border-white/40 hover:bg-white/10"
                    }`}
                    role="button"
                    tabIndex={0}
                    aria-label="Choisir un CV à importer"
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            fileInputRef.current?.click();
                        }
                    }}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        accept=".pdf,.docx,.pptx,.png,.jpg,.jpeg"
                        onChange={handleChange}
                    />
                    <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center text-white mb-4">
                        <ArrowUpTrayIcon aria-hidden="true" className="w-8 h-8" />
                    </div>
                    <p className="text-lg font-medium text-white mb-1">
                        Choisissez un CV ou déposez le fichier ici
                    </p>
                    <p className="text-sm text-slate-400">
                        Formats supportés : PDF, DOCX, PPTX, PNG, JPG (max 5MB)
                    </p>
                </div>
            )}

            {/* Lot 4: Diagnostic riche et explicatif des erreurs d'import */}
            {!isLoading && visibleError && (
                <div role="alert" className="mt-6 w-full rounded-2xl border border-red-500/30 bg-red-950/20 p-5 text-sm text-red-200 shadow-xl">
                    <div className="flex items-start gap-3">
                        <div className="p-2 rounded-xl bg-red-500/20 text-red-400 shrink-0">
                            <AlertCircle className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                            <h4 className="text-base font-bold text-red-100">
                                Échec à l&apos;étape « {failedStep.label} »
                            </h4>
                            <p className="font-semibold text-red-100 text-xs mt-1">Échec de l&apos;import du CV</p>
                            <p className="mt-1 text-xs text-red-200">{mainErrorMessage}</p>
                            
                            {causeText && (
                                <div className="mt-2 text-xs text-red-200 bg-red-900/30 border border-red-500/20 rounded-lg px-3 py-1.5">
                                    <strong>Cause :</strong> {causeText}
                                </div>
                            )}

                            {/* Traçabilité : correlation ID et code d'erreur sans fuite sensible */}
                            <div className="mt-2.5 flex flex-wrap items-center gap-3 text-[11px] text-red-300/80 font-mono">
                                {correlationJobId && (
                                    <span className="px-2 py-0.5 rounded bg-black/40 border border-red-500/20">
                                        ID de traitement : {correlationJobId}
                                    </span>
                                )}
                                {errorCode && (
                                    <span className="px-2 py-0.5 rounded bg-black/40 border border-red-500/20 uppercase">
                                        Code: {errorCode}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {importProgress?.steps && importProgress.steps.length > 0 && (
                        <div className="w-full my-3 bg-slate-900/90 border border-slate-700/80 rounded-xl p-3 shadow-xl space-y-2 text-left">
                            {stepsToRender.map((step) => {
                                let icon = <span className="flex items-center justify-center w-5 h-5 rounded-full border border-slate-700 text-slate-500 text-[11px]">○</span>;
                                let textClass = "text-slate-400";
                                if (step.status === "COMPLETED") {
                                    icon = <span className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 font-bold text-[11px]">✓</span>;
                                    textClass = "text-slate-200";
                                } else if (step.status === "PARTIAL") {
                                    icon = <span aria-label="Étape partielle" className="flex items-center justify-center w-5 h-5 rounded-full bg-amber-500/20 text-amber-300 font-bold text-[11px]">!</span>;
                                    textClass = "text-amber-200 font-semibold";
                                } else if (step.status === "FAILED") {
                                    icon = <span className="flex items-center justify-center w-5 h-5 rounded-full bg-rose-500/20 text-rose-400 font-bold text-[11px]">✕</span>;
                                    textClass = "text-rose-300 font-semibold";
                                } else if (step.status === "SKIPPED") {
                                    icon = <span className="flex items-center justify-center w-5 h-5 rounded-full bg-slate-800 text-slate-600 text-[11px]">⊘</span>;
                                    textClass = "text-slate-500";
                                }
                                return (
                                    <div
                                        key={step.id}
                                        className={`flex items-start justify-between gap-3 p-1.5 rounded-lg ${
                                            step.status === "FAILED" ? "bg-rose-500/10 border border-rose-500/30" : ""
                                        }`}
                                    >
                                        <div className="flex items-start gap-2.5">
                                            <div className="mt-0.5 shrink-0">{icon}</div>
                                            <div>
                                                <div className={`text-xs ${textClass}`}>
                                                    {step.label}
                                                    {step.duration != null && step.status === "COMPLETED" && (
                                                        <span className="ml-1.5 text-[11px] font-normal text-slate-400">
                                                            — {formatStepDuration(step.duration)} — Terminé
                                                        </span>
                                                    )}
                                                    {step.status === "FAILED" && (
                                                        <span className="ml-1.5 text-[11px] font-normal text-rose-400">
                                                            — En erreur
                                                        </span>
                                                    )}
                                                </div>
                                                {step.status === "FAILED" && (
                                                    <div className="text-[10px] text-rose-300 mt-0.5 font-mono">
                                                        {step.error || step.message || "Erreur lors de l'exécution"}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="mt-4 flex flex-wrap items-center gap-3">
                        {Boolean(lastImportResult?.draft || importError?.draft) && onOpenBuilder && (
                            <button
                                type="button"
                                onClick={onOpenBuilder}
                                className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold transition-colors shadow-md flex items-center gap-1.5"
                            >
                                Ouvrir le CV Builder
                            </button>
                        )}
                        {onRetry && (
                            <button
                                type="button"
                                onClick={onRetry}
                                className="px-4 py-2 rounded-lg bg-lenvia-blue hover:bg-blue-600 text-white text-xs font-semibold transition-colors shadow-sm flex items-center gap-1.5"
                            >
                                <ArrowPathIcon className="w-3.5 h-3.5" />
                                Réessayer l&apos;import
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => {
                                if (fileInputRef.current) fileInputRef.current.value = "";
                                if (onRetry) onRetry();
                            }}
                            className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-slate-200 text-xs font-medium transition-colors"
                        >
                            Choisir un autre fichier
                        </button>
                    </div>
                </div>
            )}

            {!isLoading && importWarnings.some(w => typeof w === 'string' && w.startsWith("Seuil quotidien d'imports IA atteint")) && (
                <div role="status" className="mt-4 rounded-xl border border-blue-400/30 bg-blue-500/10 px-4 py-3 text-sm text-blue-100">
                    Seuil quotidien d'imports IA atteint. Cette information ne bloque pas votre import.
                </div>
            )}
            {!isLoading && hasWarnings && importWarnings.some(w => !(typeof w === 'string' && w.startsWith("Seuil quotidien d'imports IA atteint"))) && (
                <div className="mt-4 w-full rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
                    <p className="font-semibold text-amber-50">Votre CV a été importé. Certaines informations nécessitent votre vérification. Vous pouvez maintenant les corriger dans le CV Builder.</p>
                    <ul className="mt-2 space-y-1 text-amber-100/90 text-xs">
                        {importWarnings.filter(w => !(typeof w === 'string' && w.startsWith("Seuil quotidien d'imports IA atteint"))).map((warning, index) => {
                            const text = getWarningUserMessage(warning);
                            const code = typeof warning === "object" && warning !== null ? warning.code : warning;
                            return (
                                <li key={`${code || text}-${index}`}>- {text}</li>
                            );
                        })}
                    </ul>
                    {onOpenBuilder && (
                        <div className="mt-3">
                            <button
                                type="button"
                                onClick={onOpenBuilder}
                                className="px-4 py-2 rounded-lg bg-lenvia-blue hover:bg-blue-600 text-white text-xs font-semibold transition-colors shadow-md inline-flex items-center gap-1.5"
                            >
                                Ouvrir le CV Builder
                            </button>
                        </div>
                    )}
                </div>
            )}

            {!isLoading && shouldShowProviderBadge && (
                <div className="mt-4 flex w-full justify-center">
                    <div className="inline-flex flex-col items-center rounded-full border border-slate-700 bg-slate-800/70 px-4 py-2 text-center leading-tight">
                        <span className="text-xs font-medium text-slate-100">⚡ {providerTitle}</span>
                        {providerSubtitle && (
                            <span className="mt-0.5 text-[11px] text-slate-400">{providerSubtitle}</span>
                        )}
                    </div>
                </div>
            )}
            
            {!isLoading && (
                <div className="mt-8 flex items-center justify-center gap-3 text-sm text-slate-400">
                    <DocumentTextIcon className="w-5 h-5" />
                    <span>L&apos;extraction garantit la normalisation des compétences et un mapping format Lenvia.</span>
                </div>
            )}
        </div>
    );
};

export default CvImportStep;
